import React, { useRef, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import type { InputRef, TableColumnsType, TableColumnType } from "antd";
import { Button, Checkbox, Input, Space, Table, notification } from "antd";
import { Form } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import { dashboardRoutes } from "../routeConfig";
import axios from "axios";
import { useEffect, useCallback } from "react";
import dayjs from "dayjs";

interface TableDataType {
     key: string;
     no: number;
     code: string;
     menu: string;
}

const useTableSearchProps = <T extends TableDataType>() => {
     const [searchText, setSearchText] = useState("");
     const [searchedColumn, setSearchedColumn] = useState("");
     const searchInput = useRef<InputRef>(null);

     const handleSearch = (selectedKeys: string[], confirm: FilterDropdownProps["confirm"], dataIndex: keyof T) => {
          confirm();
          setSearchText(selectedKeys[0]);
          setSearchedColumn(dataIndex as string);
     };

     const handleReset = (clearFilters: () => void) => {
          clearFilters();
          setSearchText("");
     };

     const getColumnSearchProps = (dataIndex: keyof T): TableColumnType<T> => ({
          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
               <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                         ref={searchInput}
                         placeholder={`Search ${String(dataIndex)}`}
                         value={selectedKeys[0]}
                         onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                         onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                         style={{ marginBottom: 8, display: "block" }}
                    />
                    <Space>
                         <Button
                              type="primary"
                              onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                              icon={<SearchOutlined />}
                              size="small"
                              style={{ width: 90 }}>
                              Search
                         </Button>
                         <Button
                              onClick={() => clearFilters && handleReset(clearFilters)}
                              size="small"
                              style={{ width: 90 }}>
                              Reset
                         </Button>
                         <Button
                              type="link"
                              size="small"
                              onClick={() => {
                                   confirm({ closeDropdown: false });
                                   setSearchText((selectedKeys as string[])[0]);
                                   setSearchedColumn(dataIndex as string);
                              }}>
                              Filter
                         </Button>
                         <Button
                              type="link"
                              size="small"
                              onClick={() => {
                                   close();
                              }}>
                              close
                         </Button>
                    </Space>
               </div>
          ),
          filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />,
          onFilter: (value, record) =>
               String(record[dataIndex])
                    .toLowerCase()
                    .includes((value as string).toLowerCase()),
          filterDropdownProps: {
               onOpenChange(open) {
                    if (open) {
                         setTimeout(() => searchInput.current?.select(), 100);
                    }
               },
          },
          render: (text) =>
               searchedColumn === String(dataIndex) ? (
                    <Highlighter
                         highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                         searchWords={[searchText]}
                         autoEscape
                         textToHighlight={text ? text.toString() : ""}
                    />
               ) : (
                    text
               ),
     });

     return { getColumnSearchProps };
};

const backendUrl = process.env.REACT_APP_BACKEND_URL;

export const useNextUserId = () => {
     const [nextUserId, setNextUserId] = useState<string | null>(null);
     const [loading, setLoading] = useState<boolean>(false);
     const [error, setError] = useState<string | null>(null);

     const fetchNextUserId = async () => {
          setLoading(true);
          setError(null);
          if (!backendUrl) {
               console.error("REACT_APP_BACKEND_URL is not defined!");
               setError("Konfigurasi backend URL tidak ditemukan.");
               setLoading(false);
               return;
          }
          try {
               const response = await axios.get(`${backendUrl}/api/user-management/next-user-id`);
               setNextUserId(response.data.nextUserId);
          } catch (err: any) {
               console.error("Error fetching next user ID:", err);
               setError(err.response?.data?.message || "Gagal mengambil User ID berikutnya.");
               setNextUserId(null);
          } finally {
               setLoading(false);
          }
     };

     const resetNextUserId = () => {
          setNextUserId(null);
          setLoading(false);
          setError(null);
     };

     return { nextUserId, loading, error, fetchNextUserId, resetNextUserId };
};

export const useUserManagementLogic = () => {
     const [form] = Form.useForm();
     const { nextUserId, loading, error, fetchNextUserId, resetNextUserId } = useNextUserId();
     const [autosaveStatus, setAutosaveStatus] = useState<"idle" | "processing" | "error" | "stopped">("idle");
     const [selectedMenuListKeys, setSelectedMenuListKeys] = useState<string[]>([]);
     const [menuUserItems, setMenuUserItems] = useState<TableDataType[]>([]);
     const [disabledMenuListKeys, setDisabledMenuListKeys] = useState<string[]>([]);
     const [warehouseOptions, setWarehouseOptions] = useState<Array<{ value: string; label: string }>>([]);
     const [selectedMenuUserKeys, setSelectedMenuUserKeys] = useState<string[]>([]);
     const [isCopying, setIsCopying] = useState(false);


     useEffect(() => {
          const disabledKeys = menuUserItems.map(item => item.key);
          setDisabledMenuListKeys(disabledKeys);
     }, [menuUserItems]);

     const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
     const debouncedFunction = useCallback((func: (...args: any[]) => void, delay: number) => {
          return (...args: any[]) => {
               if (debounceTimeoutRef.current) {
                    clearTimeout(debounceTimeoutRef.current);
               }
               debounceTimeoutRef.current = setTimeout(() => func(...args), delay);
          };
     }, []);

     const fetchWarehouseData = useCallback(async () => {
          if (!backendUrl) {
               console.error("REACT_APP_BACKEND_URL is not defined!");
               return;
          }
          try {
               const response = await axios.get(`${backendUrl}/api/user-management/warehouses`);
               const options = response.data.map((item: any) => ({
                    value: item.warehouse_code,
                    label: item.warehouse_name,
               }));
               setWarehouseOptions([{ value: "", label: "--  Empty --" }, ...options]);
          } catch (err) {
               console.error("Error fetching warehouse data:", err);
          }
     }, []);

     const handleRefreshMenuList = useCallback(() => {
          setSelectedMenuListKeys([]);
          notification.info({
               message: 'Menu List Refreshed',
               placement: 'topRight',
               duration: 2
          });
     }, []);

     useEffect(() => {
          fetchWarehouseData();
     }, [fetchWarehouseData]);

     const saveFormData = useCallback(async (values: any) => {
          const {
               kode_grup1,
               nama,
               users,
               password,
               jabatan,
               expired_date,
               status,
               warehouse_code,
               tgl_login,
               tgl_logout,
               otoritas,
          } = values;

          setAutosaveStatus("processing");

          const formattedExpiredDate = expired_date ? dayjs(expired_date).format("YYYY-MM-DD") : null;

          const mappedOtoritasValues: string[] = [];
          const selectedOtoritas = otoritas || [];

          if (selectedOtoritas.includes("admin")) {
               mappedOtoritasValues.push("01");
          }
          if (selectedOtoritas.includes("users")) {
               mappedOtoritasValues.push("02");
          }
          if (selectedOtoritas.includes("approval")) {
               mappedOtoritasValues.push("03");
          }
          if (selectedOtoritas.includes("EDP")) {
               mappedOtoritasValues.push("77");
          }
          const mappedOtoritas = mappedOtoritasValues.join(";");
          const menuOtoritasCodes = menuUserItems.map(item => item.code).join(';');

          let statusToSend = status;
          if (status === "Active") {
               statusToSend = "1";
          } else if (status === "Deactive") {
               statusToSend = "2";
          }

          const payload = {
               kode_grup1,
               nama,
               users,
               password,
               jabatan,
               expired_date: formattedExpiredDate,
               status: statusToSend,
               warehouse_code,
               tgl_login,
               tgl_logout,
               otoritas: mappedOtoritas,
               menu_otoritas: menuOtoritasCodes,
          };

          try {
               const backendUrl = process.env.REACT_APP_BACKEND_URL;
               if (!backendUrl) {
                    console.error("REACT_APP_BACKEND_URL is not defined!");
                    setAutosaveStatus("error");
                    return;
               }
               await axios.post(`${backendUrl}/api/user-management/save`, payload);
               setAutosaveStatus("idle");
          } catch (err) {
               console.error("Autosave error:", err);
               setAutosaveStatus("error");
          }
     }, [menuUserItems]);

     const debouncedSaveFormData = useCallback(debouncedFunction(saveFormData, 1000), [
          debouncedFunction,
          saveFormData,
     ]);

     useEffect(() => {
          const currentFormValues = form.getFieldsValue();
          if (currentFormValues.kode_grup1 || currentFormValues.nama) {
               debouncedSaveFormData(currentFormValues);
          }
     }, [menuUserItems, debouncedSaveFormData, form]);

     useEffect(() => {
          const storedUserData = sessionStorage.getItem('selectedUserData');
          if (storedUserData) {
               const userData = JSON.parse(storedUserData);

               let displayStatus = "";
               if (userData.status === "1") {
                    displayStatus = "Active";
               } else if (userData.status === "2") {
                    displayStatus = "Deactive";
               } else {
                    displayStatus = userData.status;
               }

               const formData = {
                    ...userData,
                    expired_date: userData.expired_date ? dayjs(userData.expired_date) : null,
                    status: displayStatus,
                    otoritas: userData.otoritas ? userData.otoritas.split(';').map((code: string) => {
                         if (code === '01') return 'admin';
                         if (code === '02') return 'users';
                         if (code === '03') return 'approval';
                         if (code === '77') return 'EDP';
                         return '';
                    }).filter(Boolean) : [],
               };
               form.setFieldsValue(formData);


               if (userData.menu_otoritas) {
                    const menuCodes = userData.menu_otoritas.split(';');
                    const userMenus: TableDataType[] = dashboardRoutes
                         .filter(route => menuCodes.includes(route.id))
                         .map((route, index) => ({
                              key: route.id,
                              no: index + 1,
                              code: route.id,
                              menu: route.menuName,
                         }));
                    setMenuUserItems(userMenus);
               }


               sessionStorage.removeItem('selectedUserData');
          } else {
               fetchNextUserId();
          }
     }, []);

     useEffect(() => {
          if (nextUserId && !form.getFieldValue("kode_grup1")) {
               form.setFieldsValue({ kode_grup1: nextUserId });
          }
     }, [nextUserId, form]);

     const handleNameChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
          const nameValue = e.target.value;
          const currentKodeGrup1 = form.getFieldValue("kode_grup1");
          form.setFieldsValue({ nama: nameValue });
          if (nameValue) {
               form.setFieldsValue({ status: "Active" });
          } else {
               form.setFieldsValue({ status: "" });
          }

          if (currentKodeGrup1) {
               return;
          }

          if (nameValue) {
               await fetchNextUserId();
          } else {
               form.setFieldsValue({ kode_grup1: "" });
               resetNextUserId();
               setAutosaveStatus("stopped");
          }
     };

     const handleFormValuesChange = useCallback(
          (changedValues: any, allValues: any) => {
               const currentKodeGrup1 = allValues.kode_grup1;

               if (currentKodeGrup1) {
                    debouncedSaveFormData(allValues);
               } else if (allValues.nama) {
                    debouncedSaveFormData(allValues);
               }
          },
          [debouncedSaveFormData],
     );

     const handleCopyMenu = useCallback(() => {
          if (selectedMenuListKeys.length === 0) return;

          setIsCopying(true);
          setTimeout(() => {
               const allMenuListData = dashboardRoutes.map((route, index) => ({
                    key: route.id,
                    no: index + 1,
                    code: route.id,
                    menu: route.menuName,
               }));

               const itemsToCopy = allMenuListData.filter(item => selectedMenuListKeys.includes(item.key));

               setMenuUserItems(prevItems => {
                    const newItems = [...prevItems];
                    itemsToCopy.forEach(item => {
                         if (!newItems.some(existingItem => existingItem.key === item.key)) {
                              newItems.push(item);
                         }
                    });
                    newItems.sort((a, b) => a.no - b.no);
                    return newItems;
               });

               setSelectedMenuListKeys([]);
               setIsCopying(false);
               notification.success({
                    message: 'Proses Add Menu, Success !',
                    placement: 'topRight',
                    duration: 3
               });
          }, 1500);

     }, [selectedMenuListKeys]);

     const handleDeleteMenuUser = useCallback(() => {
          setMenuUserItems(prevItems => {
               const updatedItems = prevItems.filter(item => !selectedMenuUserKeys.includes(item.key));

               return updatedItems.map((item, index) => ({ ...item, no: index + 1 }));
          });
          setSelectedMenuUserKeys([]);
     }, [selectedMenuUserKeys]);

     return {
          form,
          autosaveStatus,
          isCopying,
          handleRefreshMenuList,
          handleNameChange,
          handleFormValuesChange,
          nextUserId,
          loading,
          error,
          selectedMenuListKeys,
          setSelectedMenuListKeys,
          menuUserItems,
          handleCopyMenu,
          disabledMenuListKeys,
          warehouseOptions,
          selectedMenuUserKeys,
          setSelectedMenuUserKeys,
          handleDeleteMenuUser
     };
};

export const MenuList: React.FC<{
     selectedKeys: string[];
     setSelectedKeys: React.Dispatch<React.SetStateAction<string[]>>;
     disabledKeys: string[];
}> = ({ selectedKeys, setSelectedKeys, disabledKeys }) => {
     const { getColumnSearchProps } = useTableSearchProps<TableDataType>();

     const data: TableDataType[] = React.useMemo(() => {
          return dashboardRoutes.map((route, index) => ({
               key: route.id,
               no: index + 1,
               code: route.id,
               menu: route.menuName,
          }));
     }, []);

     const columns: TableColumnsType<TableDataType> = [
          {
               key: "select",
               render: (_, record) => (
                    <Checkbox
                         value={record.key}
                         checked={selectedKeys.includes(record.key)}
                         onChange={(e) => {
                              if (e.target.checked) {
                                   setSelectedKeys(prev => [...prev, record.key]);
                              } else {
                                   setSelectedKeys(prev => prev.filter(key => key !== record.key));
                              }
                         }}
                         disabled={disabledKeys.includes(record.key)}
                    />
               ),
               width: "8%",
          },
          { title: "No", dataIndex: "no", key: "no", width: "15%" },
          { title: "Code", dataIndex: "code", key: "code", ...getColumnSearchProps("code"), width: "20%" },
          {
               title: "Menu",
               dataIndex: "menu",
               key: "menu",
               ...getColumnSearchProps("menu"),
               sorter: (a, b) => a.menu.localeCompare(b.menu),
               sortDirections: ["descend", "ascend"],
          },
     ];

     return <Table<TableDataType> columns={columns} dataSource={data} scroll={{ y: 240 }} pagination={false} />;
};

export const MenuUser: React.FC<{ menuUserItems: TableDataType[]; selectedKeys: string[]; setSelectedKeys: React.Dispatch<React.SetStateAction<string[]>>; }> = ({ menuUserItems, selectedKeys, setSelectedKeys }) => {
     const { getColumnSearchProps } = useTableSearchProps<TableDataType>();

     const columns: TableColumnsType<TableDataType> = [
          {
               key: "select",
               render: (_, record) => (
                    <Checkbox
                         value={record.key}
                         checked={selectedKeys.includes(record.key)}
                         onChange={(e) => {
                              if (e.target.checked) {
                                   setSelectedKeys(prev => [...prev, record.key]);
                              } else {
                                   setSelectedKeys(prev => prev.filter(key => key !== record.key));
                              }
                         }}
                    />
               ),
               width: "8%",
          },
          { title: "No", dataIndex: "no", key: "no", width: "15%" },
          { title: "Code", dataIndex: "code", key: "code", width: "25%", ...getColumnSearchProps("code") },
          {
               title: "Menu",
               dataIndex: "menu",
               key: "menu",
               ...getColumnSearchProps("menu"),
               sorter: (a, b) => a.menu.localeCompare(b.menu),
               sortDirections: ["descend", "ascend"],
          },
     ];

     return <Table<TableDataType> columns={columns} dataSource={menuUserItems} scroll={{ y: 240 }} pagination={false} />;
};